"use client"

import {useEffect, useState} from "react";
import {useTranslations} from "next-intl";
import {cn} from "@/lib/utils";
import {AppSwitch} from "@/app/components/common/app-switch/AppSwitch";
import {MainCtaBtn} from "@/app/components/common/main-cta-btn/MainCtaBtn";
import {AppSeparator} from "@/app/components/common/app-separator/AppSeparator";
import {applyConsentDecision} from "@/lib/analytics/mixpanel";
import {CONSENT_MODE, ConsentChoice, hasConsentDecision} from "@/lib/analytics/consent";

/**
 * Cookie consent banner (AC-711).
 *
 * Gates Mixpanel and nothing else today — GA and Customer.io still fire
 * regardless of what the visitor picks here. That gap is deliberate and scoped;
 * see `lib/analytics/consent.ts`. It must be closed before this ships to
 * EU/EEA/UK traffic.
 *
 * Declining is exactly as cheap as accepting: "Reject all" sits next to "Accept
 * all" with the same weight and the same number of clicks. A banner where the
 * refusal is buried behind "Manage preferences" is the specific dark pattern the
 * GDPR/ePrivacy guidance calls out, and it would make the consent invalid — so
 * the two buttons stay symmetric.
 *
 * Rendering is client-only and deferred to an effect: the decision lives in a
 * cookie, and reading it during render would make the server and client markup
 * disagree. Returning `null` until mounted means the banner fades in a tick late
 * rather than hydrating wrong.
 */
export default function CookieConsentBanner() {
    const t = useTranslations("CookieConsent");

    const [isOpen, setIsOpen] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isCustomizing, setIsCustomizing] = useState(false);
    const [analytics, setAnalytics] = useState(true);
    const [marketing, setMarketing] = useState(true);

    // Mount. `implied` mode means the environment has opted out of the gate
    // entirely, so there is nothing to ask.
    useEffect(() => {
        if (CONSENT_MODE === "implied" || hasConsentDecision()) return;

        const id = setTimeout(() => setIsOpen(true), 0);
        return () => clearTimeout(id);
    }, []);

    // Enter transition: mount hidden, then flip on the next tick so the browser
    // has a frame to transition FROM. Setting both in one commit would paint the
    // banner already-visible and skip the animation.
    useEffect(() => {
        if (!isOpen) return;

        const id = setTimeout(() => setIsVisible(true), 60);
        return () => clearTimeout(id);
    }, [isOpen]);

    if (!isOpen) return null;

    const decide = (choice: ConsentChoice) => {
        applyConsentDecision(choice);
        setIsVisible(false);
        // Unmount only after the exit transition; the cookie is already written,
        // so nothing depends on this timer completing.
        setTimeout(() => setIsOpen(false), 300);
    };

    const acceptAll = () => decide({analytics: true, marketing: true});
    const rejectAll = () => decide({analytics: false, marketing: false});
    const saveChoices = () => decide({analytics, marketing});

    return (
        <div
            role="dialog"
            aria-modal="false"
            aria-label={t("title")}
            // Survives an open Radix modal, which is a reachable state: the banner
            // is up, the visitor clicks "Sign in", and AuthDialog opens on top.
            //
            // Radix hides everything outside a modal from assistive tech via the
            // `aria-hidden` package — every other body child, this banner included,
            // gets aria-hidden="true" — and sets `body { pointer-events: none }`.
            // The banner would be both unreadable and unclickable, with no decision
            // made and no way to make one.
            //
            // `aria-live` is the one element type that package deliberately skips
            // (theKashey/aria-hidden#10), and `pointer-events-auto` overrides the
            // inherited `none`. Together they keep the banner usable over any modal.
            //
            // Trade-off, accepted: Radix closes a modal on outside pointer-down, so
            // answering the banner while a dialog is open also dismisses that dialog.
            aria-live="polite"
            className={cn(
                "fixed z-9999 bg-dark-gray pointer-events-auto",
                "bottom-0 left-0 right-0 rounded-t-[18px]",
                "md:bottom-4 md:left-4 md:right-auto md:w-[420px] md:rounded-[18px]",
                "p-[16px] md:p-[20px]",
                "transition-all duration-300 ease-out",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
            )}
            style={{
                boxShadow: "0 8px 40px rgba(0,0,0,0.65), 0 0 0 1px rgba(175,156,44,0.28)",
                paddingBottom: "max(16px, env(safe-area-inset-bottom))",
            }}
        >
            <h2 className="text-noble-black-0 font-bold text-[15px] leading-tight">{t("title")}</h2>
            <p className="text-noble-black-300 text-[12px] leading-relaxed mt-[8px]">{t("description")}</p>

            {isCustomizing && (
                <div className="mt-[16px]">
                    <AppSeparator/>
                    <ConsentCategoryRow
                        title={t("necessaryTitle")}
                        description={t("necessaryDescription")}
                        checked
                        disabled
                    />
                    <ConsentCategoryRow
                        title={t("analyticsTitle")}
                        description={t("analyticsDescription")}
                        checked={analytics}
                        onCheckedChange={setAnalytics}
                    />
                    <ConsentCategoryRow
                        title={t("marketingTitle")}
                        description={t("marketingDescription")}
                        checked={marketing}
                        onCheckedChange={setMarketing}
                    />
                    <AppSeparator className="mb-[16px]"/>
                </div>
            )}

            <div className="flex items-center gap-[8px] mt-[16px]">
                <MainCtaBtn className="flex-1 h-[40px]" onClick={acceptAll}>
                    {t("acceptAll")}
                </MainCtaBtn>
                <button
                    onClick={rejectAll}
                    className="flex-1 h-[40px] rounded-[8px] border border-noble-black-500 text-noble-black-0 text-[12px] font-semibold cursor-pointer"
                >
                    {t("rejectAll")}
                </button>
            </div>

            {isCustomizing ? (
                <button
                    onClick={saveChoices}
                    className="w-full mt-[10px] text-noble-black-300 text-[12px] font-medium underline cursor-pointer"
                >
                    {t("save")}
                </button>
            ) : (
                <button
                    onClick={() => setIsCustomizing(true)}
                    className="w-full mt-[10px] text-noble-black-300 text-[12px] font-medium underline cursor-pointer"
                >
                    {t("customize")}
                </button>
            )}
        </div>
    );
}

interface ICategoryRowProps {
    title: string;
    description: string;
    checked: boolean;
    disabled?: boolean;
    onCheckedChange?: (checked: boolean) => void;
}

function ConsentCategoryRow({title, description, checked, disabled, onCheckedChange}: ICategoryRowProps) {
    return (
        <div className="flex items-start justify-between gap-[12px] py-[12px]">
            <div className="min-w-0">
                <p className="text-noble-black-0 text-[13px] font-semibold">{title}</p>
                <p className="text-noble-black-300 text-[11px] leading-relaxed mt-[2px]">{description}</p>
            </div>
            <AppSwitch
                // The locked "necessary" toggle must not read as an interactive one.
                className="mt-[2px] shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                checked={checked}
                disabled={disabled}
                onCheckedChange={onCheckedChange}
                aria-label={title}
            />
        </div>
    );
}
